/**
 * TanStack Query hooks over `marketplace.*`.
 *
 * Publish mutation fires a localised success toast on resolve so the
 * call-site (Blueprint builder PublishDialog) does not have to
 * duplicate the feedback at each invocation.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { marketplace } from '@/api/endpoints/marketplace';
import type { MarketplaceFilters, PublishInput } from '@/api/types';
import { useToast } from '@/components/Toaster';
import { useLocaleStore } from '@/stores/localeStore';

import { vaultKeys } from './useVault';

// `locale` is part of every query key because the official products
// pre-seeded in `@/data/defaultProducts` resolve their name /
// tagline / description per-locale at fetch time. Without locale in
// the key, switching languages would keep showing the prior
// language's strings until staleTime expired.
export const marketplaceKeys = {
  list: (filters: MarketplaceFilters, locale: string) =>
    ['marketplace', 'list', filters, locale] as const,
  detail: (id: string, locale: string) =>
    ['marketplace', 'detail', id, locale] as const,
};

export function useMarketplaceList(filters: MarketplaceFilters) {
  const locale = useLocaleStore((s) => s.locale);
  return useQuery({
    queryKey: marketplaceKeys.list(filters, locale),
    queryFn: () => marketplace.list(filters),
    staleTime: 30_000,
  });
}

export function useMarketplaceDetail(id: string | undefined) {
  const locale = useLocaleStore((s) => s.locale);
  return useQuery({
    queryKey: marketplaceKeys.detail(id ?? '', locale),
    queryFn: () => (id ? marketplace.get(id) : Promise.resolve(null)),
    enabled: Boolean(id),
  });
}

export function usePublishToMarketplace() {
  const qc = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (input: PublishInput) => marketplace.publish(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['marketplace'] });
      void qc.invalidateQueries({ queryKey: vaultKeys.snapshot });
      toast.success(t('toast.published'));
    },
  });
}

export function useResetMarketplace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: marketplace.reset,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['marketplace'] });
    },
  });
}
